import { Component, Input, ViewChild } from '@angular/core';
import { TipsReportItem } from '../../models/reports.model';
import { TipsReportTileComponent } from '../tips-report-tile/tips-report-tile.component';
import { dateString } from 'projects/common/src/public-api';
import { DynamicListComponent } from '../dynamic-list/dynamic-list.component';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { Pagination } from '../../services/pagination.service';
import { shareReplay, take, tap } from 'rxjs/operators';
import { BusinessProfile } from 'projects/common/src/lib/models/business-profile.model';
import { limitStep } from '../../services/reports-service-extras';
import { ViewAsService } from '../../services/view-as.service';
import { SupabaseService } from '../../services/supabase.service';
import { BusinessService } from '../../services/business.service';
import { ReportsService } from '../../services/reports.service';
import { BehaviorSubject } from 'rxjs';

function tipItemsToPaginationList(items: TipsReportItem[], kind: 'Pending' | 'Paid') {
  return items.map(item => ({
    componentClass: TipsReportTileComponent,
    header: dateString(item.createdAt),
    args: { item, kind }
  }));
}

@Component({
  selector: 'app-tips-report-items',
  templateUrl: './tips-report-items.component.html',
  styleUrls: ['./tips-report-items.component.scss']
})
export class TipsReportItemsComponent {

  @Input() current!: 'Pending' | 'Paid';

  @ViewChild('pendingList') pendingList!: DynamicListComponent<TipsReportTileComponent>;
  @ViewChild('pendingDateFilterList') pendingDateFilterList!: DynamicListComponent<TipsReportTileComponent>;
  @ViewChild('paidList') paidList!: DynamicListComponent<TipsReportTileComponent>;
  @ViewChild('paidDateFilterList') paidDateFilterList!: DynamicListComponent<TipsReportTileComponent>;

  pendingPagination!: Pagination<TipsReportItem>;
  pendingDateFilterPagination!: Pagination<TipsReportItem>;
  paidPagination!: Pagination<TipsReportItem>;
  paidDateFilterPagination!: Pagination<TipsReportItem>;

  loading$ = new BehaviorSubject<boolean>(true);

  dateFilter$ = this.reportsService.selectedDateRangeForEvent$.pipe(
    tap(async dates => {
      if (dates) {
        this.loadItems('Pending', 0, undefined, dates, true);
        this.loadItems('Paid', 0, undefined, dates, true);
      } else {
        this.pendingDateFilterList?.clear();
        this.paidDateFilterList?.clear();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true})
  );

  constructor(
    private reportsService: ReportsService,
    private businessService: BusinessService,
    private supabaseService: SupabaseService,
    private viewAsService: ViewAsService,
  ) { }

  async ngOnInit() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    this.pendingPagination = this.createPagination(business.businessId, 'tips_pending_report', 'tips_pen')
      .on(
        'UPDATE',
        'payment',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'client_invoice',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingList)
      );
    this.pendingDateFilterPagination = this.createPagination(business.businessId, 'tips_pending_report', 'tips_pen_d')
      .on(
        'UPDATE',
        'payment',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'client_invoice',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingDateFilterList)
      );
    this.paidPagination = this.createPagination(business.businessId, 'tips_paid_report', 'tips_paid')
      .on(
        'UPDATE',
        'payment',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'client_invoice',
        _ => this.handleChange('PendPaiding')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidList)
      );
    this.paidDateFilterPagination = this.createPagination(business.businessId, 'tips_paid_report', 'tips_paid_d')
      .on(
        'UPDATE',
        'payment',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'client_invoice',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidDateFilterList)
      );

    await this.loadItems('Pending', 0, business, undefined, false);
    await this.loadItems('Paid', 0, business, undefined, false);
    this.loading$.next(false);
  }

  ngOnDestroy() {
    this.pendingPagination?.destroy();
    this.pendingDateFilterPagination?.destroy();
    this.paidPagination?.destroy();
    this.paidDateFilterPagination?.destroy();
  }

  async loadItems(
    type: 'Pending' | 'Paid',
    inOffset = 0,
    business?: BusinessProfile,
    dates?: [Date, Date] | null,
    dateList?: boolean,
    updateList?: boolean
  ) {
    if (!business)
      business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    if (dateList && !dates)
      dates = await this.dateFilter$.pipe(take(1)).toPromise();
    if (!dates)
      dates = [business.createdAt, new Date()];

    const selectedUsers = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();

    const pagination = type === 'Pending'
      ? !dateList
        ? this.pendingPagination
        : this.pendingDateFilterPagination
      : !dateList
        ? this.paidPagination
        : this.paidDateFilterPagination;

    const list = type === 'Pending'
      ? !dateList
        ? this.pendingList
        : this.pendingDateFilterList
      : !dateList
        ? this.paidList
        : this.paidDateFilterList;

    const inLimit = updateList && limitStep < list.getBottomCount ? list.getBottomCount : limitStep;

    const items = await pagination.get({
      startTime: dates[0],
      endTime: dates[1],
      rowLimit: inLimit,
      offsetCount: inOffset,
      selectedUsers: selectedUsers ?? []
    });

    if (items.length < inLimit && list.canLoadMoreBottom)
      list.canLoadMoreBottom = false;

    if (!inOffset)
      list.clear();
    list?.addAtBottom(tipItemsToPaginationList(items, type));
  }

  async handleChange(type: string, dateList = false) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const dates = await this.dateFilter$.pipe(take(1)).toPromise();

    this.loadItems(
      type as 'Pending' | 'Paid',
      0,
      business!,
      dateList ? dates : null,
      dateList,
      true
    );
  }

  handleWorkflowChange(payload: RealtimePostgresChangesPayload<any>, list: DynamicListComponent<TipsReportTileComponent>) {
    const data = payload.new;
    list.updateItems(component => {
      const report = component.instance.item;
      if (report.wId === data.id)
        report.jobType = data.jobType;
      return false;
    });
  }

  createPagination(businessId: string, fn: string, cname: string) {
    return new Pagination<TipsReportItem>(
      this.supabaseService,
      fn,
      businessId,
      cname
    );
  }
}
